import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, FileText, Clock, CheckCircle, XCircle, Eye, Trash2, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import apiClient from '../../api/client';

const RFQDocumentsList = ({ opportunityId, onViewDocument }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (opportunityId) {
      loadDocuments();
    }
  }, [opportunityId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/v1/rfq/opportunities/${opportunityId}/documents`);
      if (response.data.success) {
        setDocuments(response.data.data);
      }
    } catch (error) {
      console.error('Error loading RFQ documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;

    try {
      const response = await apiClient.delete(`/api/v1/rfq/documents/${docId}`);
      if (response.data.success) {
        toast({
          title: 'Document Deleted',
          description: 'RFQ document has been removed',
        });
        loadDocuments();
      }
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'Failed to delete document',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
      case 'processing':
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <Clock className="w-3 h-3 mr-1" />
            Processing
          </Badge>
        );
      case 'extracted':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <Eye className="w-3 h-3 mr-1" />
            Needs Review
          </Badge>
        );
      case 'applied':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Applied
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'xlsx' || fileType === 'xls') {
      return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    }
    return <FileText className="w-5 h-5 text-red-500" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading RFQ documents...</span>
      </div>
    );
  }

  if (documents.length === 0) {
    return null; // Don't show anything if no RFQ documents
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        <FileSpreadsheet className="w-4 h-4" />
        RFQ Documents ({documents.length})
      </h4>
      
      <div className="space-y-2">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {getFileIcon(doc.file_type)}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{doc.filename}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{formatDate(doc.uploaded_at)}</span>
                  {doc.parts_count > 0 && (
                    <span>• {doc.parts_count} parts</span>
                  )}
                  {doc.extraction_confidence && (
                    <span>• {Math.round(doc.extraction_confidence * 100)}% confidence</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {getStatusBadge(doc.status)}
              
              {doc.status === 'extracted' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewDocument?.(doc.id)}
                >
                  Review
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(doc.id, doc.filename)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RFQDocumentsList;
